import axios from "axios";
import config from "config";
import { Middleware } from "@reduxjs/toolkit";
import Auth from "@aws-amplify/auth";
import { RootState } from "app/rootReducer";
import { AuthLevel } from "features/auth/reducer";
import { getPersistedData, PersistedData } from "utils/persist";
import { version } from "../../package.json";

// request type
export interface Request {
    data?: any,
    endpoint: string,
    method: "delete" | "get" | "patch" | "post" | "put",
}

// paginated response
export interface Paginated<T> {
    data: T[],
    totalCount: number,
}

// auth level (synced with store via middleware)
let authLevel: AuthLevel;
export const authLevelUpdater: Middleware<{}, RootState> = ({ getState }) => (next) => (action) => {
    const result = next(action);
    authLevel = getState().auth.level;
    return result;
};

// external token
let externalToken: string;
export const setExternalToken = (token: string) => { externalToken = token; };

// get authorization token / header
export const getAuthToken = async () => {
    if (authLevel === AuthLevel.User) {
        const session = await Auth.currentSession();
        return session.getIdToken().getJwtToken(); // use aws-provided auto refresh
    }
    if (authLevel === AuthLevel.Dashboard) {
        return externalToken || getPersistedData(PersistedData.DashboardToken);
    }
    if (authLevel === AuthLevel.External) {
        return externalToken;
    }
    return config.api.guestToken; // guest token for unauthorized api access
};
const getAuthHeader = async () => `Bearer ${await getAuthToken()}`;

// make request
export const request = async <T>({ data, endpoint, method }: Request): Promise<T> => {
    // convert and execute axios request
    const baseURL = `${config.api.url}${config.api.version}`;
    const dataOrParams = ["get", "delete"].includes(method) ? "params" : "data";
    const icludePlaySportVersion = (endpoint === "auth/me") || (endpoint === "/general/sport-forms") || (endpoint === "/profile");
    const headers: any = {
        Authorization: await getAuthHeader(),
        "x-playsports-platform": `dashboard-${version}`,
    };
    // Add versio number > 1.7.0 for Mindfullness sportForm
    if (icludePlaySportVersion) {
        headers["x-playsports-version"] = "1.7.1";
    }
    return axios.request<T>({
        baseURL,
        [dataOrParams]: data,
        headers,
        method,
        url: endpoint,
    })
        .then((res) => res.data)
        .catch((err) => { throw err.response?.data || err.response; });
};
